import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { Loader2, Plus, Clock, DollarSign, FileText, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { projectId } from '../utils/supabase/info';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { ScrollArea } from './ui/scroll-area';

interface BaselineTemplatesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  firmId: string;
  accessToken: string;
  onCreated: () => void;
}

interface BaselineTemplate {
  name: string;
  description: string;
  category: 'migration' | 'implementation' | 'fast_track' | 'health_checkup' | 'custom';
  defaultScope: string;
  defaultDeliverables: string[];
  standardDurationWeeks: number;
  estimatedHours: number;
  pricingModel: 'hourly' | 'fixed' | 'hybrid';
  defaultHourlyRate?: number;
  defaultFixedPrice?: number;
  assumptions: string[];
  exclusions: string[];
}

const CATEGORY_LABELS: Record<string, { label: string; color: string }> = {
  migration: { label: 'Migration', color: 'bg-blue-100 text-blue-700' },
  implementation: { label: 'Implementation', color: 'bg-green-100 text-green-700' },
  fast_track: { label: 'Fast Track', color: 'bg-purple-100 text-purple-700' },
  health_checkup: { label: 'Health Checkup', color: 'bg-orange-100 text-orange-700' },
  custom: { label: 'Custom', color: 'bg-gray-100 text-gray-700' },
};

export function BaselineTemplatesDialog({
  open,
  onOpenChange,
  firmId,
  accessToken,
  onCreated,
}: BaselineTemplatesDialogProps) {
  const [baselineTemplates, setBaselineTemplates] = useState<BaselineTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [addingTemplateIndex, setAddingTemplateIndex] = useState<number | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [existingTemplates, setExistingTemplates] = useState<Array<{ name: string }>>([]);

  useEffect(() => {
    if (open) {
      loadTemplates();
      loadExistingTemplates();
    }
  }, [open]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/engagement-types/baseline-templates`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setBaselineTemplates(data.templates || []);
      } else {
        console.error('Failed to load baseline templates');
        toast.error('Failed to load baseline templates');
      }
    } catch (error) {
      console.error('Error loading baseline templates:', error);
      toast.error('Error loading baseline templates');
    } finally {
      setLoading(false);
    }
  };

  const loadExistingTemplates = async () => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/firms/${firmId}/engagement-types`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setExistingTemplates(data.engagementTypes || []);
      }
    } catch (error) {
      console.error('Error loading existing templates:', error);
    }
  };

  const handleAddTemplate = async (template: BaselineTemplate, index: number) => {
    try {
      // Check if template with this name already exists
      const existingMatch = existingTemplates.find(
        (existing) => existing.name === template.name
      );

      if (existingMatch) {
        const confirmed = confirm(
          `"${template.name}" is already in your catalog.\n\nDo you want to add it again? It will be added with a number suffix so you can customize it separately.`
        );

        if (!confirmed) {
          return;
        }

        // Find the next available number suffix
        let suffix = 2;
        let newName = `${template.name} (${suffix})`;
        
        while (existingTemplates.some((existing) => existing.name === newName)) {
          suffix++;
          newName = `${template.name} (${suffix})`;
        }

        template = { ...template, name: newName };
      }

      setAddingTemplateIndex(index);
      
      console.log('[BaselineTemplates] Adding template to catalog:', {
        templateName: template.name,
        firmId,
      });
      
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/firms/${firmId}/engagement-types`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify(template),
        }
      );

      if (response.ok) {
        const result = await response.json();
        console.log('[BaselineTemplates] Template added successfully:', result);
        toast.success(`Added "${template.name}" to your catalog`);
        
        // Wait a moment for KV store to propagate, then refresh
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Refresh existing templates list and notify parent
        loadExistingTemplates();
        onCreated();
      } else {
        const errorData = await response.text();
        console.error('[BaselineTemplates] Failed to add template:', {
          status: response.status,
          statusText: response.statusText,
          error: errorData,
        });
        toast.error(`Failed to add template: ${response.statusText}`);
      }
    } catch (error) {
      console.error('[BaselineTemplates] Error adding template:', error);
      toast.error('Error adding template');
    } finally {
      setAddingTemplateIndex(null);
    }
  };

  const formatPrice = (pricingModel: string, hourlyRate?: number, fixedPrice?: number) => {
    if (pricingModel === 'hourly') {
      return `$${hourlyRate || 0}/hr`;
    } else if (pricingModel === 'fixed') {
      return `$${(fixedPrice || 0).toLocaleString()} fixed`;
    } else {
      return `$${hourlyRate || 0}/hr (cap: $${(fixedPrice || 0).toLocaleString()})`;
    }
  };

  const filteredTemplates = selectedCategory === 'all'
    ? baselineTemplates
    : baselineTemplates.filter(t => t.category === selectedCategory);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] lg:max-w-6xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Baseline Engagement Templates</DialogTitle>
          <DialogDescription>
            Pre-configured engagement templates to jump-start your service catalog. 
            Customize them after adding to your catalog.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="size-8 animate-spin text-gray-400" />
          </div>
        ) : (
          <>
            <Tabs value={selectedCategory} onValueChange={setSelectedCategory}>
              <div className="overflow-x-auto -mx-6 px-6">
                <TabsList className="inline-flex w-auto min-w-full">
                  <TabsTrigger value="all" className="whitespace-nowrap">
                    All ({baselineTemplates.length})
                  </TabsTrigger>
                  <TabsTrigger value="migration" className="whitespace-nowrap">
                    Migration ({baselineTemplates.filter(t => t.category === 'migration').length})
                  </TabsTrigger>
                  <TabsTrigger value="implementation" className="whitespace-nowrap">
                    Implementation ({baselineTemplates.filter(t => t.category === 'implementation').length})
                  </TabsTrigger>
                  <TabsTrigger value="fast_track" className="whitespace-nowrap">
                    Fast Track ({baselineTemplates.filter(t => t.category === 'fast_track').length})
                  </TabsTrigger>
                  <TabsTrigger value="health_checkup" className="whitespace-nowrap">
                    Health Check ({baselineTemplates.filter(t => t.category === 'health_checkup').length})
                  </TabsTrigger>
                  <TabsTrigger value="custom" className="whitespace-nowrap">
                    Custom ({baselineTemplates.filter(t => t.category === 'custom').length})
                  </TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value={selectedCategory} className="mt-4">
                <ScrollArea className="h-[500px] pr-4">
                  <div className="space-y-4">
                    {filteredTemplates.map((template, index) => {
                      const categoryInfo = CATEGORY_LABELS[template.category];
                      
                      return (
                        <Card key={index} className="hover:shadow-md transition-shadow">
                          <CardContent className="p-6">
                            {/* Header */}
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <h3 className="font-semibold">{template.name}</h3>
                                  <Badge className={categoryInfo.color}>
                                    {categoryInfo.label}
                                  </Badge>
                                </div>
                                <p className="text-sm text-muted-foreground">
                                  {template.description}
                                </p>
                              </div>
                              <Button
                                onClick={() => handleAddTemplate(template, index)}
                                disabled={addingTemplateIndex === index}
                                className="gap-2"
                              >
                                {addingTemplateIndex === index ? (
                                  <>
                                    <Loader2 className="size-4 animate-spin" />
                                    Adding...
                                  </>
                                ) : (
                                  <>
                                    <Plus className="size-4" />
                                    Add to Catalog
                                  </>
                                )}
                              </Button>
                            </div>

                            {/* Key metrics */}
                            <div className="grid grid-cols-3 gap-4 mb-4 pb-4 border-b">
                              <div className="text-center">
                                <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground mb-1">
                                  <Clock className="size-3" />
                                  Duration
                                </div>
                                <div className="font-semibold">
                                  {template.standardDurationWeeks} {template.standardDurationWeeks === 1 ? 'week' : 'weeks'}
                                </div>
                              </div>
                              <div className="text-center">
                                <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground mb-1">
                                  <FileText className="size-3" />
                                  Hours
                                </div>
                                <div className="font-semibold">
                                  {template.estimatedHours}h
                                </div>
                              </div>
                              <div className="text-center">
                                <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground mb-1">
                                  <DollarSign className="size-3" />
                                  Pricing
                                </div>
                                <div className="font-semibold text-xs">
                                  {formatPrice(template.pricingModel, template.defaultHourlyRate, template.defaultFixedPrice)}
                                </div>
                              </div>
                            </div>

                            {/* Deliverables */}
                            <div className="mb-3">
                              <div className="text-xs font-medium text-muted-foreground mb-2">
                                Deliverables ({template.defaultDeliverables.length})
                              </div>
                              <div className="flex flex-wrap gap-1">
                                {template.defaultDeliverables.slice(0, 5).map((deliverable, idx) => (
                                  <Badge key={idx} variant="secondary" className="text-xs">
                                    {deliverable}
                                  </Badge>
                                ))}
                                {template.defaultDeliverables.length > 5 && (
                                  <Badge variant="secondary" className="text-xs">
                                    +{template.defaultDeliverables.length - 5} more
                                  </Badge>
                                )}
                              </div>
                            </div>

                            {/* Scope preview */}
                            <div className="mb-3">
                              <div className="text-xs font-medium text-muted-foreground mb-1">
                                Scope Preview
                              </div>
                              <p className="text-xs text-muted-foreground line-clamp-3">
                                {template.defaultScope}
                              </p>
                            </div>

                            {/* Assumptions & Exclusions count */}
                            <div className="flex gap-4 text-xs text-muted-foreground">
                              <div className="flex items-center gap-1">
                                <CheckCircle className="size-3" />
                                {template.assumptions.length} assumptions
                              </div>
                              <div className="flex items-center gap-1">
                                <CheckCircle className="size-3" />
                                {template.exclusions.length} exclusions
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </ScrollArea>
              </TabsContent>
            </Tabs>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}